import { canvasAt, jpegBlob, loadImage } from "@/utils/imageToJpeg";

// Local image checks for FT-220 damage control. Coordinates are normalized after
// EXIF orientation has been applied by the decoder.
export const ANALYSIS_SIZE = 512;
export const MIN_LONG_SIDE = 1600;
export const BRIGHTNESS_MIN = 40;
export const BRIGHTNESS_MAX = 215;
export const OVEREXPOSED_LEVEL = 250;
export const OVEREXPOSED_MAX_SHARE = 0.25;
export const LAPLACIAN_MIN = 60;
export const PREVIEW_MAX_SIDE = 1600;
export const PREVIEW_QUALITY = 0.9;

export function grayscale(data) {
  const pixels = data.data || data;
  const count = Math.floor(pixels.length / 4);
  const result = new Uint8Array(count);
  for (let i = 0; i < count; i += 1) {
    const p = i * 4;
    result[i] = Math.round(0.299 * pixels[p] + 0.587 * pixels[p + 1] + 0.114 * pixels[p + 2]);
  }
  return result;
}

export function meanBrightness(gray) {
  if (!gray || !gray.length) return 0;
  let sum = 0;
  for (let i = 0; i < gray.length; i += 1) sum += gray[i];
  return sum / gray.length;
}

export function overexposedShare(gray, level = OVEREXPOSED_LEVEL) {
  if (!gray || !gray.length) return 0;
  let count = 0;
  for (let i = 0; i < gray.length; i += 1) if (gray[i] > level) count += 1;
  return count / gray.length;
}

export function laplacianVariance(gray, width, height) {
  if (!gray || width < 3 || height < 3 || gray.length < width * height) return 0;
  let sum = 0;
  let sumSquares = 0;
  let count = 0;
  for (let y = 1; y < height - 1; y += 1) {
    for (let x = 1; x < width - 1; x += 1) {
      const i = y * width + x;
      const lap = gray[i - width] + gray[i + width] + gray[i - 1] + gray[i + 1] - 4 * gray[i];
      sum += lap;
      sumSquares += lap * lap;
      count += 1;
    }
  }
  return count ? sumSquares / count - (sum / count) ** 2 : 0;
}

export function assessQuality({ gray, width, height, originalWidth, originalHeight }) {
  const brightness = meanBrightness(gray);
  const overexposed = overexposedShare(gray);
  const laplacian = laplacianVariance(gray, width, height);
  const lowResolution = Math.max(originalWidth || 0, originalHeight || 0) < MIN_LONG_SIDE;
  const checks = {
    brightness, overexposedShare: overexposed, laplacianVariance: laplacian,
    tooDark: brightness < BRIGHTNESS_MIN,
    tooBright: brightness > BRIGHTNESS_MAX,
    overexposed: overexposed > OVEREXPOSED_MAX_SHARE,
    blurry: laplacian < LAPLACIAN_MIN || lowResolution,
  };
  const reasons = [];
  if (checks.blurry) reasons.push("blurry");
  if (checks.tooDark) reasons.push("too_dark");
  if (checks.overexposed || checks.tooBright) reasons.push("overexposed");
  return { ok: reasons.length === 0, reasons, checks, lowResolution };
}

export async function decodeOriented(blob, deps = {}) {
  const bitmapFactory = deps.createImageBitmap || (typeof createImageBitmap === "function" ? createImageBitmap : null);
  if (bitmapFactory) {
    try { return await bitmapFactory(blob, { imageOrientation: "from-image" }); } catch (e) { /* Image fallback */ }
  }
  return loadImage(blob, deps, "image_decode");
}

export async function analyzeBlob(blob, deps = {}, decoded) {
  const owned = !decoded;
  const source = decoded || await decodeOriented(blob, deps);
  try { return await analyzeDecoded(source, deps); }
  finally { if (owned && source.close) source.close(); }
}

export async function analyzeDecoded(decoded, deps = {}) {
  const width = decoded.width || decoded.naturalWidth;
  const height = decoded.height || decoded.naturalHeight;
  const scale = ANALYSIS_SIZE / Math.max(width, height);
  const analysisWidth = Math.max(1, Math.round(width * scale));
  const analysisHeight = Math.max(1, Math.round(height * scale));
  const { canvas, context } = canvasAt(analysisWidth, analysisHeight,
    deps,
    { error: "canvas_context", validate: false, releaseOnError: true, contextOptions: { willReadFrequently: true } });
  try {
    context.drawImage(decoded.image || decoded, 0, 0, analysisWidth, analysisHeight);
    const gray = grayscale(context.getImageData(0, 0, analysisWidth, analysisHeight));
    const quality = assessQuality({ gray, width: analysisWidth, height: analysisHeight, originalWidth: width, originalHeight: height });
    return { width, height, ...quality };
  } finally { canvas.width = 0; canvas.height = 0; }
}

export async function makePreview(blob, deps = {}, decoded) {
  const owned = !decoded;
  const source = decoded || await decodeOriented(blob, deps);
  try { return await makeDecodedPreview(source, deps); }
  finally { if (owned && source.close) source.close(); }
}

export async function makeDecodedPreview(decoded, deps = {}) {
  const width = decoded.width || decoded.naturalWidth;
  const height = decoded.height || decoded.naturalHeight;
  const scale = Math.min(1, PREVIEW_MAX_SIDE / Math.max(width, height));
  const targetWidth = Math.max(1, Math.round(width * scale));
  const targetHeight = Math.max(1, Math.round(height * scale));
  const { canvas, context } = canvasAt(targetWidth, targetHeight,
    deps, { error: "canvas_context", validate: false, releaseOnError: true });
  try {
    context.drawImage(decoded.image || decoded, 0, 0, targetWidth, targetHeight);
    return await jpegBlob(canvas, PREVIEW_QUALITY);
  } finally { canvas.width = 0; canvas.height = 0; }
}
